import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { Header } from 'components/common/header/Header';
import { Button } from 'components/common/button/Button';
import { MeetingRepository } from 'repository/meetingRepository';
import { ScheduleRepository } from 'repository/scheduleRepository';
import './MeetingCreate.css';

const STORAGE_KEY = 'meeting_create_data';

export const MeetingCreateStep2 = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isCreating, setIsCreating] = useState(false);

    // sessionStorage에서 저장된 데이터 로드
    const loadSavedData = () => {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved) : {};
    };

    const savedData = loadSavedData();

    const [selectedDate, setSelectedDate] = useState<Date | null>(
        savedData.selectedDate ? new Date(savedData.selectedDate) : null
    );
    const [selectedTime, setSelectedTime] = useState(savedData.selectedTime || '');
    const [selectedPlace, setSelectedPlace] = useState(savedData.selectedPlace || '');
    const [selectedPlaceInfo, setSelectedPlaceInfo] = useState(savedData.selectedPlaceInfo || null);

    // location.state에서 데이터 받기 (TimePicker, PlaceSearch, 또는 MeetingEdit에서 돌아온 경우)
    useEffect(() => {
        if (location.state?.selectedTime) {
            setSelectedTime(location.state.selectedTime);
        }
        if (location.state?.selectedPlace) {
            setSelectedPlace(location.state.selectedPlace);
            setSelectedPlaceInfo(location.state.placeInfo);
        }
        if (location.state?.selectedDate) {
            // ISO 문자열을 Date 객체로 변환
            const date = new Date(location.state.selectedDate);
            if (!isNaN(date.getTime())) {
                setSelectedDate(date);
                // 해당 날짜가 있는 월로 달력 이동
                setCurrentYear(date.getFullYear());
                setCurrentMonth(date.getMonth() + 1);
            }
        }
    }, [location.state]);

    // 데이터 변경 시 sessionStorage에 저장 (기존 데이터 보존)
    useEffect(() => {
        const existingData = loadSavedData();
        const dataToSave = {
            ...existingData,  // 기존 데이터 보존 (meetingName, description, isRegularMeeting 등)
            selectedDate: selectedDate?.toISOString(),
            selectedTime,
            selectedPlace,
            selectedPlaceInfo
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    }, [selectedDate, selectedTime, selectedPlace, selectedPlaceInfo]);

    // 현재 날짜 기준으로 달력 표시
    const today = new Date();
    const [currentYear, setCurrentYear] = useState(today.getFullYear());
    const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1);

    // 이전 달로 이동
    const handlePrevMonth = () => {
        if (currentMonth === 1) {
            setCurrentMonth(12);
            setCurrentYear(currentYear - 1);
        } else {
            setCurrentMonth(currentMonth - 1);
        }
    };

    // 다음 달로 이동
    const handleNextMonth = () => {
        if (currentMonth === 12) {
            setCurrentMonth(1);
            setCurrentYear(currentYear + 1);
        } else {
            setCurrentMonth(currentMonth + 1);
        }
    };

    const timeSlots = ['오후 6:00', '오후 6:30', '오후 7:00', '오후 7:30'];

    const isNextEnabled = selectedDate !== null && selectedTime !== '' && selectedPlace !== '';

    const handleNext = () => {
        if (isNextEnabled && !isCreating) {
            // 수정 모드 확인: isEditMode가 명시적으로 true이고, meetingId가 존재해야 함
            const isEditMode = location.state?.isEditMode === true;
            const meetingId = location.state?.meetingId;

            console.log('=== handleNext 호출 ===');
            console.log('isEditMode:', isEditMode);
            console.log('meetingId:', meetingId, 'Type:', typeof meetingId);
            console.log('location.state:', location.state);

            // 수정 모드: isEditMode가 true이고 meetingId가 유효한 문자열일 때만
            if (isEditMode && meetingId && typeof meetingId === 'string') {
                // 수정 모드: location.state에서 전달받은 기존 모임 정보 사용
                const updateData = {
                    meetingName: location.state?.meetingName || '제목 없음',
                    description: location.state?.description || '',
                    category: location.state?.category || 'ETC',
                    maxMembers: location.state?.maxMembers || 10,
                    isPublic: location.state?.isPublic !== false,
                    isRegularMeeting: location.state?.isRegularMeeting || false,
                    repeatType: location.state?.repeatType || null,
                    repeatInterval: location.state?.repeatInterval || null,
                    placeName: selectedPlaceInfo?.name || selectedPlace,
                    placeAddress: selectedPlaceInfo?.address || '',
                    latitude: selectedPlaceInfo?.lat || 0,
                    longitude: selectedPlaceInfo?.lng || 0
                };

                console.log('모임 업데이트 요청 데이터:', updateData);
                console.log('업데이트할 meetingId:', meetingId, 'Type:', typeof meetingId);

                setIsCreating(true);

                // 비동기로 처리
                (async () => {
                    try {
                        // meetingId가 문자열인지 확인
                        const validMeetingId = typeof meetingId === 'string' ? meetingId : String(meetingId);
                        console.log('변환된 meetingId:', validMeetingId);

                        // 1단계: 모임 정보 업데이트 (장소 등)
                        await MeetingRepository.onPutUpdateMeeting(validMeetingId, updateData);
                        console.log('모임 정보 업데이트 완료');

                        // 2단계: 날짜/시간이 선택되어 있으면 Schedule 처리 (생성 또는 업데이트)
                        if (selectedDate && selectedTime) {
                            // 날짜와 시간 조합
                            const meetingDate = new Date(selectedDate);
                            console.log('=== 시간 파싱 디버깅 ===');
                            console.log('selectedDate:', selectedDate);
                            console.log('selectedTime:', selectedTime);
                            console.log('meetingDate (초기):', meetingDate.toString());

                            const timeParts = selectedTime.match(/(오전|오후)\s*(\d+):(\d+)/);
                            console.log('timeParts:', timeParts);

                            if (timeParts) {
                                let hours = parseInt(timeParts[2]);
                                const minutes = parseInt(timeParts[3]);
                                console.log('파싱된 hours:', hours, 'minutes:', minutes, 'period:', timeParts[1]);

                                if (timeParts[1] === '오후' && hours !== 12) {
                                    hours += 12;
                                    console.log('오후 변환: hours =', hours);
                                } else if (timeParts[1] === '오전' && hours === 12) {
                                    hours = 0;
                                    console.log('자정 변환: hours =', hours);
                                }

                                meetingDate.setHours(hours, minutes, 0, 0);
                                console.log('setHours 후 meetingDate:', meetingDate.toString());
                                console.log('Local 시간:', meetingDate.toLocaleString('ko-KR'));
                            }

                            // 종료 시간은 시작 시간 + 2시간으로 설정
                            const endDate = new Date(meetingDate);
                            endDate.setHours(endDate.getHours() + 2);
                            console.log('endDate:', endDate.toString());

                            // meetingId 유효성 재확인
                            if (!meetingId || typeof meetingId !== 'string') {
                                console.error('❌ Schedule 처리 실패: meetingId가 유효하지 않음', meetingId);
                                throw new Error('모임 ID가 유효하지 않습니다.');
                            }

                            const scheduleData = {
                                meetingId: meetingId,
                                startDatetime: meetingDate.toISOString(),
                                endDatetime: endDate.toISOString()
                            };

                            console.log('📤 Schedule 데이터:', scheduleData);
                            console.log('  - meetingId:', scheduleData.meetingId);
                            console.log('  - startDatetime:', scheduleData.startDatetime);
                            console.log('  - endDatetime:', scheduleData.endDatetime);

                            // 기존 scheduleId가 있으면 업데이트, 없으면 생성
                            const existingScheduleId = location.state?.confirmedScheduleId;
                            console.log('기존 scheduleId:', existingScheduleId);

                            if (existingScheduleId) {
                                // Schedule 업데이트
                                console.log('Schedule 업데이트 요청:', existingScheduleId, scheduleData);
                                await ScheduleRepository.onPutUpdateSchedule(existingScheduleId, scheduleData);
                                console.log('Schedule 업데이트 완료');
                            } else {
                                // Schedule 생성 및 확정
                                console.log('Schedule 생성 요청:', scheduleData);
                                const scheduleResponse = await ScheduleRepository.onPostCreateSchedule(scheduleData);
                                console.log('Schedule 생성 응답:', scheduleResponse);

                                if (scheduleResponse?.data) {
                                    // scheduleId 추출: 문자열이면 그대로, 객체면 data 속성에서 추출
                                    const scheduleId = typeof scheduleResponse.data === 'string'
                                        ? scheduleResponse.data
                                        : scheduleResponse.data?.data || scheduleResponse.data?.scheduleId;
                                    console.log('Schedule 확정 요청:', scheduleId, 'Type:', typeof scheduleId);

                                    if (scheduleId && typeof scheduleId === 'string') {
                                        // Schedule 확정
                                        await ScheduleRepository.onPostConfirmSchedule(scheduleId);
                                        console.log('Schedule 확정 완료');

                                        // 모임에 확정된 스케줄 ID 반영
                                        await MeetingRepository.onPutUpdateMeeting(validMeetingId, {
                                            ...updateData,
                                            confirmedScheduleId: scheduleId
                                        });
                                        console.log('모임에 확정 스케줄 ID 반영 완료');
                                    } else {
                                        console.error('scheduleId 추출 실패:', scheduleResponse);
                                    }
                                }
                            }
                        }

                        // 완료 후 상세 페이지로 이동
                        setIsCreating(false);
                        navigate(`/meeting/detail/${validMeetingId}`, { replace: true });
                    } catch (error) {
                        console.error('모임 수정 중 오류 발생:', error);
                        setIsCreating(false);
                        // 에러가 발생해도 상세 페이지로 이동 (최소한 장소는 업데이트 됨)
                        const validMeetingId = typeof meetingId === 'string' ? meetingId : String(meetingId);
                        navigate(`/meeting/detail/${validMeetingId}`, { replace: true });
                    }
                })();
            } else {
                // 생성 모드
                const allData = loadSavedData();
                console.log('sessionStorage에서 로드된 데이터:', allData);
                console.log('meetingName:', allData.meetingName);

                // meetingName 필수 체크
                if (!allData.meetingName) {
                    alert('모임 이름이 없습니다. 처음부터 다시 시작해주세요.');
                    navigate('/meeting/create/step1');
                    return;
                }

                const createData = {
                    meetingName: allData.meetingName,
                    description: allData.description || '',
                    category: 'ETC',
                    placeName: selectedPlaceInfo?.name || selectedPlace,
                    placeAddress: selectedPlaceInfo?.address || '',
                    latitude: selectedPlaceInfo?.lat || 0,
                    longitude: selectedPlaceInfo?.lng || 0,
                    maxMembers: 10,
                    isPublic: true,
                    isRegularMeeting: allData.isRegularMeeting || false,
                    repeatType: allData.repeatType || null,
                    repeatInterval: allData.repeatInterval || null
                };

                console.log('모임 생성 요청 데이터:', createData);
                setIsCreating(true);

                // 비동기로 처리
                (async () => {
                    try {
                        // 1단계: 모임 생성
                        const meetingResponse = await MeetingRepository.onPostCreateMeeting(createData);
                        console.log('모임 생성 응답:', meetingResponse);
                        console.log('응답 구조:', JSON.stringify(meetingResponse, null, 2));

                        // API 응답 구조 처리: { data: meetingId } 또는 { data: { data: meetingId } }
                        const meetingId = typeof meetingResponse?.data === 'string'
                            ? meetingResponse.data
                            : meetingResponse?.data?.data;

                        console.log('추출된 meetingId:', meetingId, 'Type:', typeof meetingId);

                        if (meetingId && typeof meetingId === 'string') {
                            console.log('✅ 모임 생성 완료, meetingId:', meetingId);

                            // 2단계: 날짜/시간이 선택되어 있으면 Schedule 생성 및 확정
                            if (selectedDate && selectedTime) {
                                console.log('📅 날짜/시간이 선택되어 있어 Schedule 생성을 시작합니다.');
                                // 날짜와 시간 조합
                                const meetingDate = new Date(selectedDate);
                                console.log('=== 시간 파싱 디버깅 (생성 모드) ===');
                                console.log('selectedDate:', selectedDate);
                                console.log('selectedTime:', selectedTime);
                                console.log('meetingDate (초기):', meetingDate.toString());

                                const timeParts = selectedTime.match(/(오전|오후)\s*(\d+):(\d+)/);
                                console.log('timeParts:', timeParts);

                                if (timeParts) {
                                    let hours = parseInt(timeParts[2]);
                                    const minutes = parseInt(timeParts[3]);
                                    console.log('파싱된 hours:', hours, 'minutes:', minutes, 'period:', timeParts[1]);

                                    if (timeParts[1] === '오후' && hours !== 12) {
                                        hours += 12;
                                        console.log('오후 변환: hours =', hours);
                                    } else if (timeParts[1] === '오전' && hours === 12) {
                                        hours = 0;
                                        console.log('자정 변환: hours =', hours);
                                    }

                                    meetingDate.setHours(hours, minutes, 0, 0);
                                    console.log('setHours 후 meetingDate:', meetingDate.toString());
                                    console.log('Local 시간:', meetingDate.toLocaleString('ko-KR'));
                                }

                                // 종료 시간은 시작 시간 + 2시간으로 설정
                                const endDate = new Date(meetingDate);
                                endDate.setHours(endDate.getHours() + 2);
                                console.log('endDate:', endDate.toString());

                                // meetingId 유효성 재확인
                                if (!meetingId || typeof meetingId !== 'string') {
                                    console.error('❌ Schedule 생성 실패: meetingId가 유효하지 않음', meetingId);
                                    throw new Error('모임 ID가 유효하지 않습니다.');
                                }

                                const scheduleData = {
                                    meetingId: meetingId,
                                    startDatetime: meetingDate.toISOString(),
                                    endDatetime: endDate.toISOString()
                                };

                                console.log('📤 Schedule 생성 요청:', scheduleData);
                                console.log('  - meetingId:', scheduleData.meetingId);
                                console.log('  - startDatetime:', scheduleData.startDatetime);
                                console.log('  - endDatetime:', scheduleData.endDatetime);

                                // Schedule 생성
                                const scheduleResponse = await ScheduleRepository.onPostCreateSchedule(scheduleData);
                                console.log('Schedule 생성 응답:', scheduleResponse);

                                if (scheduleResponse?.data) {
                                    // scheduleId 추출: 문자열이면 그대로, 객체면 data 속성에서 추출
                                    const scheduleId = typeof scheduleResponse.data === 'string'
                                        ? scheduleResponse.data
                                        : scheduleResponse.data?.data || scheduleResponse.data?.scheduleId;
                                    console.log('Schedule 확정 요청:', scheduleId, 'Type:', typeof scheduleId);

                                    if (scheduleId && typeof scheduleId === 'string') {
                                        // Schedule 확정
                                        await ScheduleRepository.onPostConfirmSchedule(scheduleId);
                                        console.log('Schedule 확정 완료');

                                        // 모임에 확정된 스케줄 ID 반영
                                        await MeetingRepository.onPutUpdateMeeting(meetingId, {
                                            ...createData,
                                            confirmedScheduleId: scheduleId
                                        });
                                        console.log('모임에 확정 스케줄 ID 반영 완료');
                                    } else {
                                        console.error('scheduleId 추출 실패:', scheduleResponse);
                                    }
                                }
                            }

                            // 완료 후 공유 화면으로 이동
                            const inviteCode = `MOI-${meetingId.slice(-6)}`;
                            setIsCreating(false);
                            navigate('/meeting/share', {
                                replace: true,
                                state: {
                                    meeting: {
                                        meetingId,
                                        inviteCode
                                    }
                                }
                            });
                        } else {
                            console.error('❌ meetingId 추출 실패');
                            console.error('API 응답 전체:', meetingResponse);
                            console.error('meetingResponse.data:', meetingResponse?.data);
                            console.error('추출된 meetingId:', meetingId, 'Type:', typeof meetingId);
                            setIsCreating(false);
                            alert('모임 생성에 실패했습니다.\n서버로부터 모임 ID를 받지 못했습니다.\n브라우저 콘솔을 확인해주세요.');
                        }
                    } catch (error) {
                        console.error('모임 생성 중 오류 발생:', error);
                        setIsCreating(false);
                        alert('모임 생성에 실패했습니다.');
                    }
                })();
            }
        }
    };

    const renderCalendar = () => {
        const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
        const firstDayOfMonth = new Date(currentYear, currentMonth - 1, 1).getDay();

        const days = [];
        const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

        // Week day headers
        const headers = weekDays.map(day => (
            <div key={`header-${day}`} className="calendar-header-cell">{day}</div>
        ));

        // Empty cells for days before month starts
        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
        }

        // Days of month
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(currentYear, currentMonth - 1, day);
            const isSelected = selectedDate?.getDate() === day &&
                              selectedDate?.getMonth() === currentMonth - 1 &&
                              selectedDate?.getFullYear() === currentYear;
            const isToday = day === today.getDate() &&
                           currentMonth - 1 === today.getMonth() &&
                           currentYear === today.getFullYear();
            const isPast = date < new Date(today.getFullYear(), today.getMonth(), today.getDate());

            days.push(
                <button
                    key={day}
                    className={`calendar-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isPast ? 'past' : ''}`}
                    onClick={() => !isPast && setSelectedDate(date)}
                    disabled={isPast}
                >
                    {day}
                </button>
            );
        }

        return (
            <div className="calendar">
                <div className="calendar-header">
                    <button className="calendar-nav" onClick={handlePrevMonth}>←</button>
                    <span className="calendar-month">{currentYear}년 {currentMonth}월</span>
                    <button className="calendar-nav" onClick={handleNextMonth}>→</button>
                </div>
                <div className="calendar-grid">
                    {headers}
                    {days}
                </div>
            </div>
        );
    };

    return (
        <div className="meeting-create-wrapper">
            <Header title="날짜 및 장소" isSubPage />

            <div className="meeting-create-content">
                {/* Progress Indicator - 3 Steps */}
                <div className="progress-indicator">
                    <div className="progress-step completed">1</div>
                    <div className="progress-line active"></div>
                    <div className="progress-step completed">2</div>
                    <div className="progress-line active"></div>
                    <div className="progress-step active">3</div>
                </div>

                <div className="progress-labels three-steps">
                    <span className="progress-label completed">모임 유형</span>
                    <span className="progress-label completed">모임 정보</span>
                    <span className="progress-label active">일정 설정</span>
                </div>

                {/* Date Selection */}
                <div className="form-section">
                    <h3 className="section-title">날짜 선택</h3>
                    {renderCalendar()}
                </div>

                {/* Time Selection */}
                <div className="form-section">
                    <h3 className="section-title">시간 선택</h3>
                    <div className="time-chips">
                        {timeSlots.map(time => (
                            <button
                                key={time}
                                className={`time-chip ${selectedTime === time ? 'selected' : ''}`}
                                onClick={() => setSelectedTime(time)}
                            >
                                {time}
                            </button>
                        ))}
                        <button
                            className="time-chip direct"
                            onClick={() => navigate('/meeting/time-picker', {
                                state: {
                                    ...location.state,
                                    selectedDate: selectedDate?.toISOString(),
                                    selectedPlace,
                                    selectedPlaceInfo
                                }
                            })}
                        >
                            ⏰ 직접 입력
                        </button>
                    </div>
                    {/* 직접 입력된 시간이 preset에 없으면 표시 */}
                    {selectedTime && !timeSlots.includes(selectedTime) && (
                        <div className="selected-custom-time">
                            <div className="custom-time-label">선택한 시간</div>
                            <div className="custom-time-value">{selectedTime}</div>
                        </div>
                    )}
                </div>

                {/* Place Selection */}
                <div className="form-section">
                    <h3 className="section-title">장소 설정</h3>
                    <div className="place-search-container">
                        <input
                            type="text"
                            className="place-search-input"
                            placeholder="장소명 또는 주소 검색"
                            onClick={() => navigate('/meeting/place-search', {
                                state: {
                                    ...location.state,
                                    selectedPlace,
                                    placeInfo: selectedPlaceInfo,
                                    selectedDate: selectedDate?.toISOString(),
                                    selectedTime,
                                    returnTo: '/meeting/create/step3'
                                }
                            })}
                            readOnly
                        />
                        <span className="search-icon">🔍</span>
                    </div>

                    {selectedPlace && selectedPlaceInfo && (
                        <div className="selected-place">
                            <div className="place-icon">📍</div>
                            <div className="place-info">
                                <div className="place-name">{selectedPlaceInfo.name || selectedPlace}</div>
                                <div className="place-address">{selectedPlaceInfo.address || ''}</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Next Button */}
            <div className="meeting-create-footer">
                <Button
                    text={isCreating ? (location.state?.isEditMode ? "저장 중..." : "생성 중...") : (location.state?.isEditMode ? "저장" : "다음으로")}
                    disabled={!isNextEnabled || isCreating}
                    onPress={handleNext}
                />
            </div>
        </div>
    );
};
